import { Fragment, useEffect, useState, FC } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Dialog from '../Dialog';
import SkillsButton from './SkillsButton';
import BlockHeader from '../BlockHeader';
import moreSkillsIcon from '../../assets/icons/functional-skills/more-skills.svg';
import { startNewFunctionalSkillSession } from '../../../modules/functional_skills_api';

import mixpanel from 'mixpanel-browser';

const EVENT_NAME = 'Therapy_SkillsUI_Dashboard_Click';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;

  @media screen and (min-width: 540px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media screen and (min-width: 1280px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const DialogGrid = styled(Grid)`
  min-width: 65vw;
`;

const DialogTitle = styled.h2(({ theme }) => {
  const { color, type } = theme;

  return `
    color: ${color.primary.regular};
    font-size: ${type.size.large};
    text-transform: uppercase;
  `;
});

export type Skill = {
  name: string;
  id: number;
  new_icon_path: string;
};

export interface IFunctionalSkills {
  skills: Skill[];
}

const FunctionalSkills: FC<IFunctionalSkills> = ({ skills }) => {
  // TODO: Set up typing for redux store
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [displayedSkills, setDisplayedSkills] = useState<number>(3);
  const [functionalSkillSessionID, setFunctionalSkillSessionID] = useState(-1);
  const [allGamesDialogVisible, setAllGamesDialogVisible] = useState(false);
  const location = useLocation();

  const skillClickCallback = async (skillId: number) => {
    const newFunctionalSkillSessionResponse = await startNewFunctionalSkillSession(skillId);

    if (newFunctionalSkillSessionResponse?.data && newFunctionalSkillSessionResponse.data.success) {
      setFunctionalSkillSessionID(newFunctionalSkillSessionResponse.data.new_functional_skill_session_id);
    }
  };

  const checkWindowSize = () => {
    const width = window.innerWidth;
    let displayCount = 3;

    if (768 <= width && width < 1280) {
      displayCount = 5;
    } else if (width >= 1280) {
      displayCount = 7;
    }

    setDisplayedSkills(displayCount);
  };

  useEffect(() => {
    checkWindowSize();
  }, []);

  useEffect(() => {
    window.addEventListener('resize', checkWindowSize);

    return () => {
      window.removeEventListener('resize', checkWindowSize);
    };
  });

  const skillButtons = skills.slice(0, displayedSkills).map((skill, index) => {
    return (
      <SkillsButton
        key={`skillButton-${index}`}
        title={skill.name}
        callback={() => {
          mixpanel.track(EVENT_NAME, { Action: 'Skill', Skill: skill.name });
          skillClickCallback(skill.id);
        }}
        skillId={skill.id}
        dynamicIcon={skill.new_icon_path}
        animate={true}
      />
    );
  });

  const allSkillButtons = skills.map((skill, index) => {
    return (
      <SkillsButton
        key={`allSkillButton-${index}`}
        title={skill.name}
        callback={() => {
          mixpanel.track(EVENT_NAME, { Action: 'Skill', Skill: skill.name });
          skillClickCallback(skill.id);
        }}
        skillId={skill.id}
        dynamicIcon={skill.new_icon_path}
        animate={true}
      />
    );
  });

  return (
    <Fragment>
      <section>
        <BlockHeader>Play &amp; Practice</BlockHeader>
        {functionalSkillSessionID !== -1 && (
          <Redirect
            push
            to={{ pathname: `/play/functional-skill/${functionalSkillSessionID}`, state: { from: location.pathname } }}
          />
        )}
        <Grid>
          {skillButtons}

          {skills.length > displayedSkills && (
            <SkillsButton
              title="More skills"
              callback={() => setAllGamesDialogVisible(true)}
              staticIcon={moreSkillsIcon}
              animate={false}
            />
          )}
        </Grid>
      </section>

      <Dialog isOpen={allGamesDialogVisible} closeCallback={() => setAllGamesDialogVisible(false)}>
        <DialogTitle>Restore full skill set</DialogTitle>
        <DialogGrid>{allSkillButtons}</DialogGrid>
      </Dialog>
    </Fragment>
  );
};

export default FunctionalSkills;
